/**
 * @ccphone/contracts
 *
 * Shared names, shapes and constants for every ccphone component.
 * Behaviour is specified in docs/design; this file fixes the vocabulary.
 * Everything exported here is used by at least two packages. Keep it
 * dependency-light: zod for schemas, uuidv7 for ids, nothing else.
 */
import { z } from 'zod';
import { uuidv7 } from 'uuidv7';

// ---------------------------------------------------------------------------
// Ports, timings, limits
// ---------------------------------------------------------------------------

export const PORTS = {
  api: 3000,
  relay: 3001,
  orchestrator: 3002,
  orchestratorAriInbound: 8443,
  connector: 3003,
  journal: 3004,
  sweeper: 3005,
} as const;

export const TIMINGS = {
  /** Single-use launch token lifetime (seconds). */
  launchTokenTtlSec: 60,
  /** Session JWT lifetime (seconds): 10 h. */
  sessionJwtTtlSec: 10 * 3600,
  /** Hard cap on a session including refreshes (seconds): 14 h. */
  sessionHardCapSec: 14 * 3600,
  /** TURN credential lifetime (seconds): 12 h. */
  turnCredentialTtlSec: 12 * 3600,
  /** Relay auth frame must arrive within this many ms of the socket opening. */
  relayAuthDeadlineMs: 5000,
  /** Phone heartbeat interval (ms). */
  heartbeatIntervalMs: 15_000,
  /** Session is 'stale' after this many seconds without a heartbeat. */
  staleAfterSec: 45,
  /** Zombie ladder step: pause queues after this many seconds without a heartbeat. */
  pauseAfterSec: 90,
  /** Zombie ladder step: end the session after this many seconds without a heartbeat. */
  zombieAfterSec: 600,
  /** Sweeper skips the ladder for this long after its own or the relay's start. */
  ladderWarmupSec: 180,
  /** Inbound ring budget on the gateway (seconds). Tenant Ring Timeout must be >= 35. */
  ringTimeoutSec: 25,
  /** Rejoin window after a browser-side loss (seconds); 60 in maintenance mode. */
  rejoinWindowSec: 120,
  rejoinWindowMaintenanceSec: 60,
  /** Reconciliation: hang up Ring/Ringing channels older than this (seconds). */
  reconcileRingingMaxAgeSec: 25,
  /** Acks are cached per cid for this long (seconds). */
  ackCacheSec: 300,
  /** Web Push ring TTL (seconds) and the decline token lifetime. */
  pushRingTtlSec: 25,
  pushMissedTtlSec: 3600,
  /** ARI reload coalescing window (seconds) for new ps_registrations rows. */
  ariReloadCoalesceSec: 10,
  /** SorceryMemoryCachePopulate coalescing window (seconds). */
  sorceryPopulateCoalesceSec: 5,
  /** Yeastar OpenAPI token: lifetime 30 min, refresh at 25 min. */
  openapiTokenTtlSec: 1800,
  openapiTokenRefreshSec: 1500,
  /** Yeastar event WebSocket heartbeat (seconds); the PBX closes after 60 s of silence. */
  openapiWsHeartbeatSec: 30,
} as const;

export const LIMITS = {
  /** Lines a pop-out may have up at once; a third INVITE is rejected 486. */
  maxLines: 2,
  /** GROUP_COUNT cap enforced in cc-out per agent. */
  maxOutboundPerAgent: 3,
  /** Default and hard caps on conference parties (PBX add_member allows 9). */
  defaultConfParties: 8,
  hardConfParties: 9,
  /** Browser SIP password length (characters). */
  sipPasswordLength: 32,
  /** Outbound registration pacing per tenant (REGISTER re-arms per second). */
  registerRearmPerSec: 2,
  /** Per-tenant breaker: Rejected registrations in 10 minutes that stop re-arms. */
  rejectedBreakerCount: 3,
  rejectedBreakerWindowSec: 600,
} as const;

// ---------------------------------------------------------------------------
// Naming helpers
// ---------------------------------------------------------------------------

export const TENANT_ID = z.number().int().min(1).max(32767);
export const EXTENSION = z.string().regex(/^[0-9]{2,8}$/);
/** The canonical call id `cc-<tenantId>-<uuidv7>` (CONVENTIONS 2); cc.calls.id is the uuid part. */
export const CALL_ID = z.string().regex(/^cc-\d{1,5}-[0-9a-f-]{36}$/);

/** The kill switch in Postgres (the record): `cc.tenants.feature_flags` / `cc.agents.device_policy` key, `true` while active. */
export const KILL_SWITCH_FLAG = 'kill_switch';

export function endpointId(tenantId: number, ext: string): string {
  return `t${tenantId}-${ext}`;
}
export function upstreamEndpointId(tenantId: number, ext: string): string {
  return `t${tenantId}-${ext}-up`;
}
export function newCallId(tenantId: number): string {
  return `cc-${tenantId}-${uuidv7()}`;
}
/** cc.calls.id is the uuid part of the call id. */
export function callRowId(callId: string): string {
  const m = /^cc-\d+-([0-9a-f-]{36})$/.exec(callId);
  if (!m) throw new Error(`not a call id: ${callId}`);
  return m[1]!;
}
export const ChannelIds = {
  browser: (callId: string) => `${callId}-b`,
  browserRejoin: (callId: string) => `${callId}-b2`,
  upstream: (callId: string) => `${callId}-up`,
  consultUp: (callId: string, n = 1) => `${callId}-c${n}-up`,
  participantUp: (callId: string, n: number) => `${callId}-p${n}-up`,
} as const;
export const BridgeIds = {
  mix: (callId: string) => `${callId}-mix`,
  consult: (callId: string, n = 1) => `${callId}-c${n}`,
} as const;

export const STASIS_APP = 'ccphone';
export type StasisRole = 'upstream' | 'browser' | 'rejoin';
export const DIALPLAN = {
  upstreamContext: 'ccphone-upstream',
  browserContext: 'ccphone-browser',
  outContext: 'cc-out',
  quarantineContext: 'sbc-quarantine',
} as const;

export interface ParsedEndpoint {
  tenantId: number;
  ext: string;
  /** true for the PCE-facing endpoint `t42-1001-up` */
  upstream: boolean;
  endpointId: string;
}
const ENDPOINT_RE = /^t(\d{1,5})-(\d{2,8})(-up)?$/;
export function parseEndpointId(id: string): ParsedEndpoint | null {
  const m = ENDPOINT_RE.exec(id);
  if (!m) return null;
  return { tenantId: Number(m[1]), ext: m[2]!, upstream: m[3] === '-up', endpointId: id };
}
/** `PJSIP/t42-1001-up-0000001a` -> tenant 42, ext 1001, upstream. Local channels return null. */
export function parseChannelName(name: string): ParsedEndpoint | null {
  const m = /^PJSIP\/(t\d{1,5}-\d{2,8}(?:-up)?)-[0-9a-f]{8}$/.exec(name);
  return m ? parseEndpointId(m[1]!) : null;
}
/** `cc-42-<uuid>-b`, `-up`, `-c1-up`, `-p3-up`, `-b2` -> parts. */
export function parseChannelId(channelId: string):
  | { callId: string; kind: 'browser' | 'browser2' | 'upstream' | 'consult' | 'participant'; n?: number }
  | null {
  const m = /^(cc-\d+-[0-9a-f-]{36})-(b|b2|up|c(\d+)-up|p(\d+)-up)$/.exec(channelId);
  if (!m) return null;
  const callId = m[1]!;
  const tail = m[2]!;
  if (tail === 'b') return { callId, kind: 'browser' };
  if (tail === 'b2') return { callId, kind: 'browser2' };
  if (tail === 'up') return { callId, kind: 'upstream' };
  if (m[3]) return { callId, kind: 'consult', n: Number(m[3]) };
  return { callId, kind: 'participant', n: Number(m[4]) };
}

export function maskNumber(n: string | null | undefined): string {
  if (!n) return '';
  return n.length <= 3 ? '***' : `***${n.slice(-3)}`;
}
export function maskEmail(e: string): string {
  const [u, d] = e.split('@');
  return `${(u ?? '').slice(0, 1)}***@${d ?? ''}`;
}

// ---------------------------------------------------------------------------
// Redis keys
// ---------------------------------------------------------------------------

export const RedisKeys = {
  launchToken: (token: string) => `lt:${token}`,
  relayOut: (sid: string) => `relay:out:${sid}`,
  relayWake: 'relay:wake',
  relayStarted: 'relay:started',
  busOrchCmd: 'bus:orch:cmd',
  busYeastarEvt: 'bus:yeastar:evt',
  busControl: 'bus:control',
  agentSession: (agentId: string) => `agent:${agentId}:session`,
  agentContact: (agentId: string) => `agent:${agentId}:contact`,
  session: (sid: string) => `sess:${sid}`,
  ack: (sid: string, cid: string) => `ack:${sid}:${cid}`,
  channel: (channelId: string) => `ch:${channelId}`,
  bridge: (bridgeId: string) => `br:${bridgeId}`,
  call: (callId: string) => `call:${callId}`,
  openapiToken: (tenantId: number) => `yst:token:${tenantId}`,
  openapiTokenLock: (tenantId: number) => `yst:tokenlock:${tenantId}`,
  webhookReplay: (hash: string) => `wh:replay:${hash}`,
  ghlReplay: (hash: string) => `ghl:replay:${hash}`,
  odooNonce: (tenantId: number, nonce: string) => `odoo:nonce:${tenantId}:${nonce}`,
  rearmBucket: (tenantId: number) => `rearm:${tenantId}`,
  rejectedBreaker: (tenantId: number) => `breaker:${tenantId}`,
  nodeState: 'node:state',
  maintenance: 'maintenance',
  /** decline token single-use guard (api), SET NX for the token window */
  declineNonce: (nonce: string) => `decline:nonce:${nonce}`,
  /**
   * kill-switch mirrors the api writes ('1' while active) and launch/refresh/identity read; the orchestrator refuses
   * every originate for a killed tenant or endpoint from them (Postgres `KILL_SWITCH_FLAG` is the record)
   */
  killswitchTenant: (tenantId: number) => `killswitch:tenant:${tenantId}`,
  killswitchEndpoint: (endpoint: string) => `killswitch:ep:${endpoint}`,
  /** orchestrator liveness: SET 1 EX 10 every 5 s while its bus:orch:cmd consumer runs; the relay answers node_down when it vanishes */
  orchAlive: 'orch:alive',
  /** relay hint for the sweeper: the last socket of the session closed (15 min) */
  relayClosed: (sid: string) => `relay:closed:${sid}`,
  /** planned restart `MaintenancePlan` (api admin / ccctl) that drives the sweeper's drain */
  maintenancePlan: 'maintenance:plan',
  /** the sweeper's drain phase record */
  maintenanceDrain: 'maintenance:drain',
  /** connector's queue membership mirror (QueueMembership JSON) read by the sweeper during a connector outage */
  queueMembership: (tenantId: number, ext: string) => `yst:queues:${tenantId}:${ext}`,
} as const;

/**
 * Stream conventions (CONVENTIONS 4): every entry carries ONE field, `data`, holding the JSON
 * envelope (BusSessionEvent | PushRequest on relay:out:<sid>, BusCommand on bus:orch:cmd,
 * YeastarEvent on bus:yeastar:evt).
 */
export const BUS = {
  streamField: 'data',
  /** XADD relay:out:<sid> MAXLEN ~ */
  relayOutMaxLen: 2000,
  /** XADD bus:yeastar:evt MAXLEN ~ */
  yeastarEvtMaxLen: 50_000,
  /** consumer group on bus:orch:cmd */
  orchGroup: 'orch',
  /** consumer group of the journal on bus:yeastar:evt */
  journalGroup: 'journal',
} as const;

// ---------------------------------------------------------------------------
// Session and launch
// ---------------------------------------------------------------------------

export const SessionRole = z.enum(['phone', 'dock']);
export type SessionRole = z.infer<typeof SessionRole>;

/** Why a session ended (phone_sessions.end_reason, session.revoked). */
export const RevokeReason = z.enum(['logout', 'admin', 'zombie', 'expired', 'superseded']);
export type RevokeReason = z.infer<typeof RevokeReason>;

/** Claims of the 10 h ES256 session JWT. */
export const SessionClaims = z.object({
  iss: z.literal('ccphone-api'),
  aud: z.literal('ccphone'),
  sub: z.string(), // agent_id
  sid: z.string(), // phone_sessions.id
  tid: TENANT_ID,
  ext: EXTENSION,
  role: SessionRole,
  jti: z.string(),
  iat: z.number(),
  exp: z.number(),
  /** hard cap, seconds since epoch; refresh never extends past it */
  cap: z.number(),
});
export type SessionClaims = z.infer<typeof SessionClaims>;

export const LaunchRequest = z.object({
  /** the single-use token from the URL fragment `/launch#lt=` */
  lt: z.string().min(16),
  browser_id: z.string().optional(),
  role: SessionRole.default('phone'),
});
export type LaunchRequest = z.infer<typeof LaunchRequest>;

export const TurnCredentials = z.object({
  urls: z.array(z.string()),
  username: z.string(),
  credential: z.string(),
  /** 'all' normally; 'relay' when the tenant policy forces TURN */
  policy: z.enum(['all', 'relay']),
  expires_at: z.string(), // ISO
});
export type TurnCredentials = z.infer<typeof TurnCredentials>;

export const PhonePolicy = z.object({
  codecs: z.array(z.string()).default(['G722', 'PCMA', 'PCMU', 'opus']),
  maxLines: z.number().int().default(LIMITS.maxLines),
  iceTransportPolicy: z.enum(['all', 'relay']).default('all'),
  /** tenant policy when the agent leaves a conference with others still up */
  leaveConference: z.enum(['end_all', 'continue']).default('end_all'),
  maxConfParties: z.number().int().default(LIMITS.defaultConfParties),
  transferMethods: z.array(z.enum(['redirect', 'feature_code', 'openapi', 'gateway'])).default(['redirect']),
});
export type PhonePolicy = z.infer<typeof PhonePolicy>;

export const LaunchResponse = z.object({
  jwt: z.string(),
  refresh_token: z.string(),
  /** ISO; JWT expiry */
  expires_at: z.string(),
  /** ISO; hard cap */
  hard_cap_at: z.string(),
  /** ISO; server clock at response time so clients compute an offset */
  server_time: z.string(),
  sip: z.object({
    wss: z.string(), // wss://sip.<domain>:443/ws
    domain: z.string(), // sip.<domain> (the SIP URI host and realm)
    username: z.string(), // t42-1001
    password: z.string(), // 32 chars, per session
    /** ISO; when the broker will rotate the password (session expiry) */
    expires_at: z.string(),
  }),
  turn: TurnCredentials,
  relay: z.object({ url: z.string() }), // wss://api.<domain>/relay
  api_base: z.string(),
  policy: PhonePolicy,
  agent: z.object({
    id: z.string(),
    display_name: z.string(),
    extension: EXTENSION,
    tenant_id: TENANT_ID,
    tenant_slug: z.string(),
    crm: z.enum(['monday', 'ghl', 'odoo', 'halo', 'none']),
  }),
  /** Web Push: the VAPID application server key (base64url, uncompressed P-256 point); absent when push is not configured */
  push: z.object({ vapid_public_key: z.string().min(80) }).optional(),
});
export type LaunchResponse = z.infer<typeof LaunchResponse>;

export const RefreshRequest = z.object({
  refresh_token: z.string(),
  /** lines the pop-out has up; the SIP password is rotated only when this is 0 */
  lines_up: z.number().int().min(0).max(2).optional(),
});
export type RefreshRequest = z.infer<typeof RefreshRequest>;
export const RefreshResponse = z.object({
  jwt: z.string(),
  refresh_token: z.string(),
  expires_at: z.string(),
  server_time: z.string(),
  /** present only when the SIP password was rotated (never while a line is up) */
  sip: LaunchResponse.shape.sip.optional(),
  turn: TurnCredentials.optional(),
});
export type RefreshResponse = z.infer<typeof RefreshResponse>;

// ---------------------------------------------------------------------------
// Relay protocol: browser <-> relay
// ---------------------------------------------------------------------------

export const SipState = z.enum(['connecting', 'registered', 'unregistered', 'reconnecting', 'failed']);
export const Visibility = z.enum(['visible', 'hidden']);

export const Heartbeat = z.object({
  t: z.literal('hb'),
  sip: SipState,
  audio_ok: z.boolean(),
  visibility: Visibility,
  locked: z.boolean(),
  idle: z.boolean(),
  lines_up: z.number().int().min(0).max(2).default(0),
});
export type Heartbeat = z.infer<typeof Heartbeat>;

export const RelayAuthFrame = z.object({
  t: z.literal('auth'),
  jwt: z.string(),
  role: SessionRole,
  /** Redis stream id of the last message seen, or null for a fresh start */
  last_seq: z.string().nullable().default(null),
});
export type RelayAuthFrame = z.infer<typeof RelayAuthFrame>;

/** Yeastar extension presence values (POST /extension/update presence_status). */
export const PresenceStatus = z.enum(['available', 'away', 'business_trip', 'do_not_disturb', 'lunch_break', 'off_work']);
export type PresenceStatus = z.infer<typeof PresenceStatus>;

const cmd = <T extends string>(t: T) => z.object({ t: z.literal(t), cid: z.string().min(1) });
export const RelayCommand = z.discriminatedUnion('t', [
  cmd('dial').extend({ number: z.string(), crm_ref: z.string().optional() }),
  /** informational: the SIP accept is authoritative; lets the relay stop the ring timer early */
  cmd('answer').extend({ call_id: z.string() }),
  cmd('decline').extend({ call_id: z.string(), to_voicemail: z.boolean().default(false) }),
  cmd('hangup').extend({ call_id: z.string() }),
  cmd('hold').extend({ call_id: z.string() }),
  cmd('unhold').extend({ call_id: z.string() }),
  cmd('mute').extend({ call_id: z.string(), muted: z.boolean() }),
  cmd('transfer.blind').extend({ call_id: z.string(), target: z.string() }),
  cmd('transfer.attended.start').extend({ call_id: z.string(), target: z.string() }),
  cmd('transfer.attended.complete').extend({ call_id: z.string() }),
  cmd('conf.add').extend({ call_id: z.string(), target: z.string() }),
  cmd('conf.drop').extend({ call_id: z.string(), channel_id: z.string() }),
  cmd('conf.mute').extend({ call_id: z.string(), channel_id: z.string(), muted: z.boolean() }),
  cmd('conf.merge').extend({ call_id: z.string() }),
  cmd('swap').extend({ call_id: z.string() }),
  cmd('rejoin').extend({ call_id: z.string(), bridge_id: z.string() }),
  cmd('resume'),
  cmd('queue.pause').extend({ paused: z.boolean(), reason: z.string().optional() }),
  cmd('presence.set').extend({ status: PresenceStatus }),
  cmd('set_state').extend({ ready: z.boolean(), reason: z.string().optional() }),
  cmd('stats').extend({
    call_id: z.string(),
    jitter_ms: z.number(), loss_pct: z.number(), rtt_ms: z.number(), codec: z.string(), relayed: z.boolean(), mos: z.number(),
  }),
]);
export type RelayCommand = z.infer<typeof RelayCommand>;
export type RelayCommandType = RelayCommand['t'];

export const ClientFrame = z.union([RelayAuthFrame, Heartbeat, RelayCommand]);
export type ClientFrame = z.infer<typeof ClientFrame>;

export const NodeState = z.enum(['up', 'draining', 'restarting', 'down']);
export type NodeState = z.infer<typeof NodeState>;
export const CallState = z.enum(['ringing', 'dialing', 'answered', 'held', 'ended']);
export const CallOutcome = z.enum(['answered', 'missed', 'declined', 'busy', 'failed', 'cancelled', 'rejoin_timeout', 'media_lost', 'node_lost']);
export const CallDirection = z.enum(['inbound', 'outbound']);

export const RosterEntry = z.object({
  channel_id: z.string(),
  label: z.string(),
  number: z.string().optional(),
  kind: z.enum(['customer', 'consult', 'participant']),
  state: z.enum(['ringing', 'up', 'held', 'ended']),
  muted: z.boolean().default(false),
  talking: z.boolean().default(false),
});
export type RosterEntry = z.infer<typeof RosterEntry>;

export const CallSummary = z.object({
  call_id: z.string(),
  direction: CallDirection,
  state: CallState,
  remote_number: z.string().optional(),
  remote_name: z.string().optional(),
  queue_name: z.string().optional(),
  crm_match: z.record(z.string(), z.unknown()).optional(),
  started_at: z.string(),
  answered_at: z.string().optional(),
  /** ISO wall-clock deadline for the ring; clients derive the countdown from server_time */
  ring_deadline: z.string().optional(),
  conference: z.boolean().default(false),
  roster: z.array(RosterEntry).default([]),
  /** present while a consult leg exists */
  consult: z.object({ channel_id: z.string(), target: z.string(), state: z.string() }).optional(),
  held_by: z.enum(['agent', 'remote']).optional(),
});
export type CallSummary = z.infer<typeof CallSummary>;

const evt = <T extends string>(t: T) => z.object({ t: z.literal(t), seq: z.string().optional(), at: z.string() });
export const RelayEvent = z.discriminatedUnion('t', [
  evt('ack').extend({ cid: z.string(), ok: z.boolean(), error: z.string().optional(), data: z.unknown().optional() }),
  evt('state').extend({
    calls: z.array(CallSummary),
    held_bridges: z.array(z.object({ call_id: z.string(), bridge_id: z.string(), roster: z.array(RosterEntry), expires_at: z.string() })).default([]),
    presence: z.string().optional(),
    paused_queues: z.array(z.string()).default([]),
    node_state: NodeState,
    maintenance: z.boolean(),
    control: z.enum(['ok', 'reconnecting', 'down']).default('ok'),
    server_time: z.string(),
  }),
  evt('call.ringing').extend({ call: CallSummary }),
  evt('call.dialing').extend({ call: CallSummary }),
  evt('call.answered').extend({ call_id: z.string(), answered_at: z.string() }),
  evt('call.held').extend({ call_id: z.string(), by: z.enum(['agent', 'remote']) }),
  evt('call.resumed').extend({ call_id: z.string() }),
  evt('call.roster').extend({ call_id: z.string(), roster: z.array(RosterEntry), conference: z.boolean() }),
  evt('call.ended').extend({ call_id: z.string(), outcome: CallOutcome, cause: z.number().optional(), ended_at: z.string() }),
  evt('call.missed').extend({ call_id: z.string(), remote_number: z.string().optional(), remote_name: z.string().optional(), at: z.string() }),
  evt('call.rejoinable').extend({ call_id: z.string(), bridge_id: z.string(), roster: z.array(RosterEntry), expires_at: z.string() }),
  evt('presence').extend({ status: z.string(), paused_queues: z.array(z.string()) }),
  evt('control').extend({ ari: z.enum(['ok', 'reconnecting', 'down']) }),
  evt('node_state').extend({ state: NodeState, eta_sec: z.number().optional(), incident_id: z.string().optional(), message: z.string().optional() }),
  evt('maintenance').extend({ active: z.boolean(), message: z.string().optional(), restart_at: z.string().optional() }),
  evt('session.revoked').extend({ reason: RevokeReason, message: z.string().optional() }),
  evt('mwi').extend({ waiting: z.boolean(), new: z.number().int(), old: z.number().int() }),
]);
export type RelayEvent = z.infer<typeof RelayEvent>;
export type RelayEventType = RelayEvent['t'];

// ---------------------------------------------------------------------------
// Internal bus envelopes
// ---------------------------------------------------------------------------

export const BusCommand = z.object({
  v: z.literal(1),
  sid: z.string(),
  agent_id: z.string(),
  tenant_id: TENANT_ID,
  ext: EXTENSION,
  at: z.string(),
  cmd: RelayCommand,
});
export type BusCommand = z.infer<typeof BusCommand>;

/** Entries written to relay:out:<sid>; the relay adds `seq` from the stream id. */
export const BusSessionEvent = z.object({ v: z.literal(1), evt: RelayEvent });
export type BusSessionEvent = z.infer<typeof BusSessionEvent>;

export const BusControl = z.discriminatedUnion('t', [
  z.object({ t: z.literal('node_state'), state: NodeState, eta_sec: z.number().optional(), incident_id: z.string().optional(), message: z.string().optional() }),
  z.object({ t: z.literal('maintenance'), tenant_id: TENANT_ID.optional(), active: z.boolean(), message: z.string().optional(), restart_at: z.string().optional() }),
  z.object({ t: z.literal('session.revoked'), sid: z.string(), reason: RevokeReason }),
  z.object({ t: z.literal('relay.started'), at: z.string() }),
]);
export type BusControl = z.infer<typeof BusControl>;

/** Yeastar OpenAPI events normalised by the connector. */
export const YeastarEvent = z.object({
  v: z.literal(1),
  tenant_id: TENANT_ID,
  type: z.number().int(), // 30008, 30009, 30011, 30012, 30016, 30029
  received_at: z.string(),
  /** the raw `msg` object, already JSON-parsed; shape differs per type */
  msg: z.record(z.string(), z.unknown()),
  /**
   * Set when the connector correlated the event to one of our legs with confidence >= 60.
   * `call_id` is the canonical `cc-<tenant>-<uuid>` form (cc.yeastar_events.call_id stores the uuid part).
   */
  correlation: z.object({ call_id: CALL_ID, leg_channel_id: z.string(), confidence: z.number().int() }).optional(),
});
export type YeastarEvent = z.infer<typeof YeastarEvent>;

// ---------------------------------------------------------------------------
// Web Push payloads
// ---------------------------------------------------------------------------

export const PushPayload = z.discriminatedUnion('t', [
  z.object({
    t: z.literal('ring'),
    call_id: z.string(),
    caller_name: z.string().optional(),
    caller_number: z.string().optional(),
    queue_name: z.string().optional(),
    /** ISO wall-clock deadline (25 s from the INVITE) */
    deadline: z.string(),
    /** signed, 25 s, single-use; posted to POST /v1/calls/<id>/decline without a cookie */
    decline_token: z.string(),
    launch_url: z.string(),
  }),
  z.object({ t: z.literal('missed'), call_id: z.string(), caller_name: z.string().optional(), caller_number: z.string().optional(), at: z.string(), badge: z.number().int() }),
]);
export type PushPayload = z.infer<typeof PushPayload>;

/**
 * Web Push request on the session stream relay:out:<sid> (field `data`), recognised by the
 * `push` key where a BusSessionEvent has `evt`. Written by the orchestrator (closed-window ring,
 * missed call); the relay sends it to the agent's push subscriptions.
 */
export const PushRequest = z.object({
  v: z.literal(1),
  push: PushPayload,
  /** defaults to the session's agent */
  agent_id: z.string().optional(),
  sid: z.string().optional(),
  /** narrow the target browsers (cc.push_subscriptions.id) */
  subscription_ids: z.array(z.string()).optional(),
});
export type PushRequest = z.infer<typeof PushRequest>;

/** Every entry on relay:out:<sid>. */
export const SessionStreamEntry = z.union([BusSessionEvent, PushRequest]);
export type SessionStreamEntry = z.infer<typeof SessionStreamEntry>;

/**
 * Payload of the 25 s, single-use decline token in PushPayload.decline_token. Minted by the
 * orchestrator, verified by the api (single use via RedisKeys.declineNonce) and again by the
 * orchestrator. Wire form and HMAC: @ccphone/shared mintDeclineToken / verifyDeclineToken.
 */
export const DeclineTokenPayload = z.object({
  call_id: z.string().min(1),
  agent_id: z.string().min(1),
  /** unix seconds */
  exp: z.number().int(),
  nonce: z.string().min(8).max(64),
});
export type DeclineTokenPayload = z.infer<typeof DeclineTokenPayload>;

// ---------------------------------------------------------------------------
// Tenant feature flags and dial policy
// ---------------------------------------------------------------------------

export const TenantFeatureFlags = z.object({
  call_add_member: z.boolean().default(false),
  call_transfer: z.boolean().default(false),
  transfer_codes: z.object({ blind: z.string(), attended: z.string() }).optional(),
  pause_reason_code: z.string().default('CCPhone'),
  /** until Spike 6 pins it: how X-Signature is encoded */
  webhook_sig_encoding: z.enum(['base64', 'hex']).default('base64'),
  presence_update: z.boolean().default(true),
  display_call_feature_name: z.boolean().default(false),
});
export type TenantFeatureFlags = z.infer<typeof TenantFeatureFlags>;

export const TransferMethod = z.enum(['redirect', 'feature_code', 'openapi', 'gateway']);
export type TransferMethod = z.infer<typeof TransferMethod>;

/** Tenant call policy kept in tenants.feature_flags (no columns); both the api and the orchestrator derive it here. */
export interface TenantCallPolicy {
  leaveConference: 'end_all' | 'continue';
  /**
   * Blind-transfer methods in the order the orchestrator tries them: the tenant's
   * `transfer_methods` (default ['redirect']) minus those whose prerequisite flag is off
   * (feature_code needs transfer_codes, openapi needs call_transfer), plus 'gateway' (attended
   * completion on the gateway is always available).
   */
  transferMethods: TransferMethod[];
  /** sound:<name> played before a gateway recording starts */
  consentSound: string | null;
  mohClass: string;
  iceTransportPolicy: 'all' | 'relay';
  /** codec preference order for the pop-out; undefined = PhonePolicy default */
  codecs: string[] | undefined;
}

/**
 * Raw tenants.feature_flags jsonb -> the call policy. Unknown or malformed values fall back to
 * the defaults (never throws): the flags are operator-edited.
 * Keys: leave_conference, transfer_methods, consent_sound, moh_class, ice_transport_policy, codecs,
 * plus TenantFeatureFlags (transfer_codes, call_transfer).
 */
export function tenantCallPolicy(raw: Record<string, unknown> | null | undefined): TenantCallPolicy {
  const r = raw ?? {};
  const flags = TenantFeatureFlags.safeParse(r);
  const transferCodes = flags.success ? flags.data.transfer_codes : undefined;
  const callTransfer = flags.success ? flags.data.call_transfer : false;
  const configured = Array.isArray(r['transfer_methods'])
    ? (r['transfer_methods'] as unknown[]).filter((m): m is TransferMethod => TransferMethod.safeParse(m).success)
    : [];
  const methods: TransferMethod[] = [];
  for (const m of configured.length ? configured : (['redirect'] as TransferMethod[])) {
    if (methods.includes(m)) continue;
    if (m === 'feature_code' && !transferCodes) continue;
    if (m === 'openapi' && !callTransfer) continue;
    methods.push(m);
  }
  if (!methods.includes('gateway')) methods.push('gateway');
  const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);
  const codecs = Array.isArray(r['codecs']) && r['codecs'].every((c) => typeof c === 'string') && r['codecs'].length > 0 ? (r['codecs'] as string[]) : undefined;
  return {
    leaveConference: r['leave_conference'] === 'continue' ? 'continue' : 'end_all',
    transferMethods: methods,
    consentSound: str(r['consent_sound']),
    mohClass: str(r['moh_class']) ?? 'default',
    iceTransportPolicy: r['ice_transport_policy'] === 'relay' ? 'relay' : 'all',
    codecs,
  };
}

export const DialPolicy = z.object({
  international: z.boolean().default(false),
  premium: z.boolean().default(false),
  /** E.164 prefixes always denied */
  blocklist: z.array(z.string()).default([]),
  /** 24 h "HH:MM-HH:MM" in Australia/Sydney, empty = always */
  hours: z.string().optional(),
});
export type DialPolicy = z.infer<typeof DialPolicy>;

// ---------------------------------------------------------------------------
// Tenant PBX connections: the upstream SIP TLS profile (tenants.sbc_path, sbc_ca_file) and the
// OpenAPI endpoint and its TLS check (tenants.openapi_base_url, api_tls_pin_sha256), cc migration 0005
// ---------------------------------------------------------------------------

/**
 * Upstream SIP TLS profile of a tenant (column `tenants.sbc_path`):
 *   public  publicly trusted CA, hostname verified, from the anchor (IP-A)       -> transport-tls
 *   vpc     publicly trusted CA, hostname verified, over the VPC (peered)        -> transport-tls-vpc
 *   pca     the tenant's own private CA bundle (`sbc_ca_file`), from the anchor  -> transport-tls-pca
 *   unverified  from the anchor, TLS on but the certificate NOT verified           -> transport-tls-unverified
 *           For a PBX behind Yeastar Remote Access (<name>.ras.yeastar.com, one *.ras.yeastar.com certificate):
 *           PJSIP matches names exactly and refuses wildcards (RFC 5922 7.2), so it can never be verified. A named,
 *           audited exception (cc 0008), never a default.
 */
export const SBC_PATHS = ['public', 'vpc', 'pca', 'unverified'] as const;
export const SbcPath = z.enum(SBC_PATHS);
export type SbcPath = z.infer<typeof SbcPath>;

/**
 * The PJSIP transports infra/ansible/roles/asterisk/templates/pjsip.conf.j2 renders, by profile. The api writes
 * these names into ps_endpoints.transport and ps_registrations.transport of a tenant's upstream rows; renaming one
 * side without the other breaks every registration of the tenant.
 */
export const UPSTREAM_TRANSPORTS: Readonly<Record<SbcPath, string>> = Object.freeze({
  public: 'transport-tls',
  vpc: 'transport-tls-vpc',
  pca: 'transport-tls-pca',
  unverified: 'transport-tls-unverified',
});
export function upstreamTransportFor(path: SbcPath): string {
  return UPSTREAM_TRANSPORTS[path];
}

/**
 * pca only: the file name of the tenant's CA bundle inside the gateway's tenant CA directory
 * (`asterisk_tenant_ca_dir`, /etc/asterisk/keys/tenant-cas/, which transport-tls-pca reads as ca_list_path after
 * `openssl rehash`). A bare name: no directory, `.pem` or `.crt` (the extensions `openssl rehash` picks up).
 */
export const SbcCaFile = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,62}\.(pem|crt)$/, 'sbc_ca_file must be a bare file name ending .pem or .crt (e.g. t42.pem)');
export function defaultSbcCaFile(tenantId: number): string {
  return `t${tenantId}.pem`;
}

/**
 * SHA-256 of the DER-encoded leaf certificate a tenant's PBX presents on its OpenAPI listener, as 64 hex
 * characters. Accepts what operators paste: upper or lower case, colon separated (`openssl x509 -fingerprint
 * -sha256` prints `AB:CD:...`), surrounding whitespace; normalises to 64 lowercase hex characters.
 * Obtain it independently of the connector: `openssl s_client -connect <host>:<port> </dev/null | openssl x509
 * -outform DER | shasum -a 256` (the 64 characters before " -").
 */
export function normaliseSha256Pin(input: string): string | null {
  const v = input.trim().replace(/:/g, '').toLowerCase();
  return /^[0-9a-f]{64}$/.test(v) ? v : null;
}
export const TlsPinSha256 = z.string().transform((v, ctx) => {
  const n = normaliseSha256Pin(v);
  if (n === null) {
    ctx.addIssue({ code: 'custom', message: 'must be the SHA-256 of the DER leaf certificate: 64 hex characters, colons allowed' });
    return z.NEVER;
  }
  return n;
});

/**
 * The tenant's OpenAPI base URL when it is not `https://<pce_fqdn>/openapi/v1.0`, e.g. a PBX reached directly on
 * its own address and port (`https://170.64.197.134:8088/openapi/v1.0`) while SIP goes through a relay hostname.
 * https only, no credentials, query or fragment; a trailing slash is dropped.
 */
export const OpenApiBaseUrl = z.string().transform((v, ctx) => {
  let u: URL;
  try {
    u = new URL(v.trim());
  } catch {
    ctx.addIssue({ code: 'custom', message: 'must be an absolute https URL' });
    return z.NEVER;
  }
  if (u.protocol !== 'https:') ctx.addIssue({ code: 'custom', message: 'must use https' });
  if (u.username || u.password) ctx.addIssue({ code: 'custom', message: 'must not carry credentials' });
  if (u.search || u.hash || v.includes('?') || v.includes('#')) ctx.addIssue({ code: 'custom', message: 'must not carry a query or fragment' });
  if (!u.hostname) ctx.addIssue({ code: 'custom', message: 'must name a host' });
  return `${u.origin}${u.pathname.replace(/\/+$/, '')}`;
});
export function defaultOpenApiBaseUrl(pceFqdn: string): string {
  return `https://${pceFqdn}/openapi/v1.0`;
}
/** The base URL the connector uses for a tenant row. */
export function openApiBaseUrlOf(t: { pce_fqdn: string; openapi_base_url?: string | null }): string {
  return t.openapi_base_url ? t.openapi_base_url.replace(/\/+$/, '') : defaultOpenApiBaseUrl(t.pce_fqdn);
}

// ---------------------------------------------------------------------------
// Journal outbox (cc.journal_entries) payloads and dedupe keys
// ---------------------------------------------------------------------------

/**
 * cc.journal_entries.payload. Producers: the orchestrator (call_logged / missed_call for every
 * call it handled) and the connector (missed_call from a 30012 CDR the gateway never saw).
 * Consumer: the journal's CRM writers. Unknown keys pass through.
 */
export const JournalPayload = z.looseObject({
  /** canonical call id (orchestrator entries) */
  call_id: z.string().nullable().optional(),
  direction: CallDirection.nullable().optional(),
  tenant_id: TENANT_ID.optional(),
  agent_id: z.string().nullable().optional(),
  ext: z.string().nullable().optional(),
  extension: z.string().nullable().optional(),
  remote_number: z.string().nullable().optional(),
  remote_name: z.string().nullable().optional(),
  queue_name: z.string().nullable().optional(),
  started_at: z.string().nullable().optional(),
  ringing_at: z.string().nullable().optional(),
  answered_at: z.string().nullable().optional(),
  ended_at: z.string().nullable().optional(),
  duration_sec: z.number().nullable().optional(),
  outcome: z.string().nullable().optional(),
  hangup_by: z.string().nullable().optional(),
  conference: z.boolean().optional(),
  crm_match: z.record(z.string(), z.unknown()).nullable().optional(),
  /** CRM record the call belongs to: '12345', 12345, 'item:12345' or {type, id} */
  crm_ref: z.union([z.string(), z.number(), z.record(z.string(), z.unknown())]).nullable().optional(),
  /** recording_ready entries */
  recording_id: z.string().nullable().optional(),
  /** connector CDR entries: 'cdr_closed_window' */
  source: z.string().optional(),
  yeastar_call_id: z.string().nullable().optional(),
});
export type JournalPayload = z.infer<typeof JournalPayload>;

export const JournalDedupe = {
  /** orchestrator: one entry per call and kind */
  call: (kind: 'call_logged' | 'missed_call', callId: string) => `${kind}:${callId}`,
  /** connector and journal (30012 CDR of a missed call): one key for both producers */
  cdrMissed: (tenantId: number, yeastarCallId: string) => `missed_call:${tenantId}:${yeastarCallId}`,
} as const;

// ---------------------------------------------------------------------------
// Public api responses shared with the phone
// ---------------------------------------------------------------------------

/** GET /v1/calls?since=&state=&limit= (the pop-out's Missed/Recent list and the dock). */
export const CallListItem = z.object({
  call_id: z.string(),
  direction: CallDirection,
  state: z.string(),
  outcome: z.string().nullable(),
  remote_number: z.string().nullable(),
  remote_name: z.string().nullable(),
  queue_name: z.string().nullable().optional(),
  started_at: z.string(),
  answered_at: z.string().nullable().optional(),
  ended_at: z.string().nullable(),
  conference: z.boolean().optional(),
});
export type CallListItem = z.infer<typeof CallListItem>;
export const CallsListResponse = z.object({ calls: z.array(CallListItem), since: z.string(), server_time: z.string() });
export type CallsListResponse = z.infer<typeof CallsListResponse>;

/** RedisKeys.maintenancePlan: a planned restart; the sweeper drains from T-10 min. */
export const MaintenancePlan = z.object({
  restart_at: z.string().refine((v) => Number.isFinite(Date.parse(v)), 'restart_at must be an ISO instant'),
  message: z.string().max(300).optional(),
});
export type MaintenancePlan = z.infer<typeof MaintenancePlan>;

// ---------------------------------------------------------------------------
// Internal HTTP between services (Compose network only; Caddy refuses /internal/*).
// The full caller -> callee table is docs/INTERNAL_API.md.
// ---------------------------------------------------------------------------

/** api -> orchestrator POST /internal/endpoints/<endpointId>/revoke */
export const InternalEndpointRevokeRequest = z.object({
  sid: z.string(),
  agent_id: z.string(),
  tenant_id: TENANT_ID,
  ext: EXTENSION,
  reason: z.enum([...RevokeReason.options, 'killswitch']),
  at: z.string(),
});
export type InternalEndpointRevokeRequest = z.infer<typeof InternalEndpointRevokeRequest>;
export const InternalEndpointRevokeResponse = z.object({ ok: z.boolean(), endpoint: z.string(), hung_up: z.array(z.string()), kept: z.array(z.string()) });
export type InternalEndpointRevokeResponse = z.infer<typeof InternalEndpointRevokeResponse>;

/** api -> orchestrator POST /internal/calls/<callId>/decline (closed-window decline from a push). */
export const InternalCallDeclineRequest = z.object({
  /** the decline token from the push payload; the orchestrator verifies it again */
  token: z.string().min(16).max(1024),
  agent_id: z.string().optional(),
  tenant_id: TENANT_ID.optional(),
  ext: EXTENSION.optional(),
  source: z.enum(['push', 'api']).default('push'),
  to_voicemail: z.boolean().default(false),
  at: z.string().optional(),
});
export type InternalCallDeclineRequest = z.input<typeof InternalCallDeclineRequest>;
/** 200 ok | 403 invalid_token | 404 unknown_call | 409 bad_state */
export const InternalCallDeclineResponse = z.object({ ok: z.boolean(), result: z.enum(['ok', 'invalid_token', 'unknown_call', 'bad_state']) });
export type InternalCallDeclineResponse = z.infer<typeof InternalCallDeclineResponse>;

/** sweeper -> orchestrator GET /internal/channels?tenant=&ext= (live-call check) */
export const InternalChannel = z.object({
  id: z.string(),
  name: z.string(),
  state: z.string(),
  tenant_id: z.number().int().nullable(),
  ext: z.string().nullable(),
  call_id: z.string().nullable(),
  call_state: z.string().nullable(),
  kind: z.string().nullable(),
});
export type InternalChannel = z.infer<typeof InternalChannel>;
export const InternalChannelsResponse = z.object({ channels: z.array(InternalChannel), live_calls: z.number().int() });
export type InternalChannelsResponse = z.infer<typeof InternalChannelsResponse>;

/** sweeper -> api POST /internal/sessions/<sid>/revoke (the api's one revocation sequence) */
export const InternalSessionRevokeRequest = z.object({
  reason: z.enum(['zombie', 'expired']),
  at: z.string(),
  actor: z.literal('sweeper'),
  detail: z.record(z.string(), z.unknown()).optional(),
});
export type InternalSessionRevokeRequest = z.infer<typeof InternalSessionRevokeRequest>;
/** 200 (already_ended when the row was ended before) | 404 unknown session */
export const InternalSessionRevokeResponse = z.object({
  ok: z.boolean(),
  sid: z.string(),
  already_ended: z.boolean().optional(),
  steps: z.array(z.string()).optional(),
  warnings: z.array(z.string()).optional(),
});
export type InternalSessionRevokeResponse = z.infer<typeof InternalSessionRevokeResponse>;

/** orchestrator -> api GET /internal/crm-match?tenant=<id>&number=<e164> (300 ms budget on the caller) */
export const InternalCrmMatchResponse = z.object({
  match: z.record(z.string(), z.unknown()).nullable(),
  /** where the match came from: 'cache' (an earlier call's crm_match) or 'none' */
  source: z.string().optional(),
});
export type InternalCrmMatchResponse = z.infer<typeof InternalCrmMatchResponse>;

/** Every connector /internal answer: {ok:true, ...} or {ok:false, error, detail?}. */
export const ConnectorResult = z.looseObject({ ok: z.boolean(), error: z.string().optional(), detail: z.string().optional() });
export type ConnectorResult = z.infer<typeof ConnectorResult>;

/** sweeper -> connector POST /internal/tenants/<tid>/agent-pause (GET /queue/agent_pause on the PBX) */
export const ConnectorAgentPauseRequest = z.object({
  extension: EXTENSION,
  queue_id: z.string().min(1),
  operation: z.enum(['pause', 'unpause']),
  reason: z.string().max(64).optional(),
});
export type ConnectorAgentPauseRequest = z.infer<typeof ConnectorAgentPauseRequest>;

/** sweeper, orchestrator -> connector POST /internal/tenants/<tid>/presence (POST /extension/update {id, presence_status}) */
export const ConnectorPresenceRequest = z.object({ extension: EXTENSION, presence_status: PresenceStatus });
export type ConnectorPresenceRequest = z.infer<typeof ConnectorPresenceRequest>;

/** orchestrator -> connector POST /internal/tenants/<tid>/queue-pause (every cached queue, or `queues`) */
export const ConnectorQueuePauseRequest = z.object({
  ext: EXTENSION,
  agent_id: z.string().nullable().optional(),
  paused: z.boolean(),
  reason: z.string().max(64).optional(),
  queues: z.array(z.string()).optional(),
});
export type ConnectorQueuePauseRequest = z.infer<typeof ConnectorQueuePauseRequest>;

/** orchestrator -> connector POST /internal/tenants/<tid>/call-transfer (flag call_transfer; 409 unless correlated at 100) */
export const ConnectorTransferRequest = z.object({
  type: z.enum(['blind', 'attended']),
  call_id: z.string(),
  /** our Asterisk channel id of the leg to transfer */
  channel_id: z.string(),
  /** the correlated 30011 channel id, when known */
  yeastar_channel_id: z.string().nullable(),
  target: z.string(),
  agent_id: z.string().nullable(),
  ext: EXTENSION,
});
export type ConnectorTransferRequest = z.infer<typeof ConnectorTransferRequest>;

/** sweeper -> connector GET /internal/tenants/<tid>/agents/<ext>/queues */
export const ConnectorQueueRef = z.object({
  id: z.union([z.number().int(), z.string().min(1)]).transform(String),
  number: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
});
export const ConnectorQueuesResponse = z.object({
  ok: z.boolean(),
  extension: z.string().optional(),
  agent_id: z.string().nullable().optional(),
  ext_id: z.number().int().nullable().optional(),
  queues: z.array(ConnectorQueueRef).default([]),
  refreshed_at: z.string().nullable().optional(),
  stale: z.boolean().optional(),
  source: z.string().optional(),
});
export type ConnectorQueuesResponse = z.infer<typeof ConnectorQueuesResponse>;

// ---------------------------------------------------------------------------
// Database row shapes (cc schema) used across services
// ---------------------------------------------------------------------------

export interface TenantRow {
  id: number;
  slug: string;
  pce_fqdn: string;
  pce_firmware: string | null;
  openapi_client_id: string;
  openapi_secret_ref: string;
  webhook_secret_ref: string | null;
  webhook_path: string | null;
  egress_ips: string[];
  recording_sor: 'yeastar' | 'gateway';
  conference_mode: string;
  max_conf_parties: number;
  feature_flags: TenantFeatureFlags;
  maintenance: boolean;
  max_seats: number;
  /** upstream SIP TLS profile (SbcPath): selects the PJSIP transport of the tenant's upstream rows */
  sbc_path: SbcPath;
  /** pca only (null otherwise): CA bundle file name in the gateway's tenant CA directory */
  sbc_ca_file: string | null;
  /** null = https://<pce_fqdn>/openapi/v1.0 */
  openapi_base_url: string | null;
  /** 64 lowercase hex: the OpenAPI leaf certificate pin; null = CA + hostname verification */
  api_tls_pin_sha256: string | null;
}
export interface AgentRow {
  id: string;
  tenant_id: number;
  email: string;
  display_name: string;
  extension: string;
  yeastar_ext_id: number | null;
  ext_secret_ref: string | null;
  device_policy: { profile: string; concurrent_regs: number };
  status: 'active' | 'disabled';
}
export interface PhoneSessionRow {
  id: string;
  agent_id: string;
  launched_via: string;
  jwt_jti: string | null;
  refresh_hash: Uint8Array | null;
  issued_at: string;
  expires_at: string;
  hard_cap_at: string;
  last_heartbeat_at: string | null;
  paused_queues: string[] | null;
  ended_at: string | null;
  end_reason: string | null;
  browser_id: string | null;
  state: 'active' | 'stale' | 'paused' | 'ended';
}
export interface CallRow {
  id: string;
  tenant_id: number;
  agent_id: string;
  direction: 'inbound' | 'outbound';
  remote_number: string | null;
  remote_name: string | null;
  queue_name: string | null;
  crm_match: Record<string, unknown> | null;
  started_at: string;
  ringing_at: string | null;
  answered_at: string | null;
  ended_at: string | null;
  state: string;
  outcome: string | null;
  hangup_by: string | null;
  conference: boolean;
  yeastar_call_id: string | null;
  correlation_confidence: number | null;
}
export interface CallLegRow {
  id: string;
  call_id: string;
  kind: 'browser' | 'upstream' | 'consult' | 'participant';
  ast_channel_id: string;
  ast_bridge_id: string | null;
  endpoint: string;
  yeastar_channel_id: string | null;
  state: string;
  started_at: string | null;
  answered_at: string | null;
  ended_at: string | null;
  hangup_cause: number | null;
}
export interface JournalEntryRow {
  id: string;
  tenant_id: number;
  call_id: string | null;
  crm_connection_id: string | null;
  kind: 'call_logged' | 'missed_call' | 'recording_ready' | 'note';
  dedupe_key: string;
  payload: Record<string, unknown>;
  status: 'pending' | 'inflight' | 'done' | 'failed' | 'dead';
  attempts: number;
  next_attempt_at: string;
  crm_object_id: string | null;
  last_error: string | null;
}

// ---------------------------------------------------------------------------
// Error codes shared by api responses and relay acks
// ---------------------------------------------------------------------------

export const ErrorCodes = {
  unauthenticated: 'unauthenticated',
  sessionEnded: 'session_ended',
  maintenance: 'maintenance',
  notFound: 'not_found',
  invalidNumber: 'invalid_number',
  policyDenied: 'policy_denied',
  lineCap: 'line_cap',
  confCap: 'conf_cap',
  nodeDown: 'node_down',
  ariUnavailable: 'ari_unavailable',
  unknownCall: 'unknown_call',
  badState: 'bad_state',
  rateLimited: 'rate_limited',
  internal: 'internal',
} as const;
export type ErrorCode = (typeof ErrorCodes)[keyof typeof ErrorCodes];

/** Minimal helper for ISO timestamps with millisecond precision. */
export const nowIso = (): string => new Date().toISOString();
